// Lib
import { createSelector, createStructuredSelector } from 'reselect';
import * as Immutable from 'immutable';

// Util
import { createShallowSelector } from '../../../utils/milanoteReselect/milanoteReselect';
import { getTitle, getRootBoardProperty } from '../../../../common/elements/utils/elementPropertyUtils';
import { getThemeIsDarkMode } from '../../../user/account/accountModal/interface/themeSettings/themeSelector';
import { asObject } from '../../../../common/utils/immutableHelper';

// Selectors
import { detachedBreadcrumbIsValidSelector } from '../breadcrumbSelector';
import { isPublishPreviewModeEnabledSelector } from '../../preview/previewSelector';
import { getPublishedBreadcrumbIds } from '../../preview/previewBreadcrumbSelector';
import { getPlatformDetailsSelector } from '../../../platform/platformSelector';
import { ImMNElementMap } from '../../../../common/elements/elementModelTypes';
import {
    getCurrentUserFavoriteBoardsRootId,
    getCurrentUserSharedRootId,
    getIsSidebarPrototypeEnabled,
    getAllSharedBoardsLinkedBoardIdsSelector,
} from '../../sidebar/sidebarSelector';
import { getCurrentBoardId } from '../../../reducers/currentBoardId/currentBoardIdSelector';

const BREADCRUMB_PADDING = 56;

type BreadcrumbObj = {
    path: string[];
};

type OwnProps = {
    breadcrumbObj: BreadcrumbObj;
};

const getBreadcrumbIds = (state: Immutable.Map<string, unknown>, ownProps: OwnProps) => {
    return isPublishPreviewModeEnabledSelector(state)
        ? getPublishedBreadcrumbIds(state)
        : ownProps.breadcrumbObj.path || [];
};

// FIXME-FAVOURITES-SIDEBAR - Remove this selector once the sidebar feature is removed
const getBreadcrumbPathForFavouriteBoards = createShallowSelector(
    getBreadcrumbIds,
    (state) => state.get('elements') as ImMNElementMap,
    getCurrentUserFavoriteBoardsRootId,
    getAllSharedBoardsLinkedBoardIdsSelector,
    getCurrentUserSharedRootId,
    getCurrentBoardId,
    (path, elements, favouriteBoardsRootId, sharedLinkedBoardIds, sharedRootId, currentBoardId) => {
        let filteredPath = path.filter((elementId: string) => elementId !== favouriteBoardsRootId);

        // this is done to make sure that the shared root is always included in the path for shared boards
        if ((asObject(sharedLinkedBoardIds) as string[])?.includes(currentBoardId)) {
            filteredPath = [sharedRootId, ...filteredPath];
        }

        return filteredPath.map((elementId: string) => {
            const element = elements.get(elementId);

            const isRoot = false;
            const title = getTitle(element);

            const titleLength = (title && title.length) || 0;
            const size = BREADCRUMB_PADDING + titleLength * 5.9;

            return {
                elementId,
                title,
                isRoot,
                size,
            };
        });
    },
);

const getBreadcrumbPathForBoards = createShallowSelector(
    getBreadcrumbIds,
    (state) => state.get('elements') as ImMNElementMap,
    (path, elements) =>
        path.map((elementId: string) => {
            const element = elements.get(elementId);

            const isRoot = getRootBoardProperty(element);
            const title = isRoot ? 'Home' : getTitle(element);

            const titleLength = (title && title.length) || 0;
            const size = BREADCRUMB_PADDING + titleLength * 5.9;

            return {
                elementId,
                title,
                isRoot,
                size,
            };
        }),
);

const getBreadcrumbPath = createSelector(
    getIsSidebarPrototypeEnabled,
    getBreadcrumbPathForBoards,
    getBreadcrumbPathForFavouriteBoards,
    (isSidebarProtypeEnabled: boolean, boardsPath: string[], favouriteBoardsPath: string[]): string[] =>
        isSidebarProtypeEnabled ? favouriteBoardsPath : boardsPath,
);

const breadCrumbListSelector = createStructuredSelector({
    // @ts-ignore TS struggling with JS file types
    breadcrumbPath: getBreadcrumbPath,
    detachedBreadcrumbIsValid: detachedBreadcrumbIsValidSelector,
    isDarkModeTheme: getThemeIsDarkMode,
    platformDetails: getPlatformDetailsSelector,
});

export default breadCrumbListSelector;
